import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, getMe } from "@/lib/api";
import { applyAuthExpiredBoundary, unscopedKey } from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";

/** TanStack Query wrapper around `GET /api/me`.
 *
 * Side effects (in `useEffect` so React Strict Mode dev double-mount
 * stays idempotent):
 *   - On success: copy user + profile into the auth store, and
 *     active_scope + selection_required into the scope store. These
 *     stores are the synchronous read sources for non-React code
 *     (the api fetch wrapper, query-key builders).
 *   - On 401: apply the shared auth-expired boundary: clear the
 *     persisted token, mark auth store anonymous, clear scope, and
 *     REMOVE every authenticated/private query from the cache (not
 *     invalidate). `removeQueries` evicts entries entirely so the next
 *     subscriber goes into `pending` and has to wait for a fresh fetch
 *     — vs `invalidateQueries`, which marks data stale but keeps it in
 *     the cache, allowing a synchronous read of the previous user's data
 *     across the auth boundary. Privacy isolation needs removal;
 *     freshness needs invalidation. See `removeAuthenticatedFromCache`
 *     in `lib/queryClient.ts` for the cache primitive.
 *   - On any other error: leave the stores alone. The error bubbles
 *     to the caller via `query.error`.
 *
 * Don't read user/profile from this hook in components — read from
 * `useAuthStore(...)` and `useScopeStore(...)` so a component
 * doesn't need to know whether the cache is warm.
 */
export function useMe() {
  const applyMe = useAuthStore((s) => s.applyMePayload);
  const applyScope = useScopeStore((s) => s.applyMePayload);

  const query = useQuery({
    queryKey: unscopedKey("me"),
    queryFn: getMe,
    retry: false, // /me's 401 is informational, not a network error
    staleTime: 0,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (query.data) {
      applyMe(query.data);
      applyScope(query.data);
    }
    if (query.error instanceof ApiError && query.error.status === 401) {
      applyAuthExpiredBoundary();
    }
  }, [query.data, query.error, applyMe, applyScope]);

  return query;
}
